import React, { useMemo } from 'react';
import * as Checkbox from '@radix-ui/react-checkbox';
import { Cross2Icon, CheckIcon, BarChartIcon, BookmarkIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons';
import styles from './StrategyForm.module.css';

/**
 * Step 1 — Basic Information: strategy name + symbol selection.
 *
 * Purely presentational. All state and handlers live in the parent
 * StrategyForm orchestrator and are passed down as props.
 */
const StepBasicInfo = ({
  formData,
  updateField,
  // Symbol search
  symbolSearch,
  setSymbolSearch,
  showSymbolDropdown,
  setShowSymbolDropdown,
  symbolDropdownRef,
  filteredSymbols,
  addSymbol,
  removeSymbol,
  removeCategory,
  symbolSources,
  tradableSymbolSet,
  // Watchlist
  watchlistsLoading,
  watchlistsError,
  watchlists,
  selectedWatchlistId,
  handleSelectWatchlist,
  watchlistStocksLoading,
  watchlistStocks,
  // Group / Index
  groupsLoading,
  groupsError,
  groups,
  selectedGroupIdx,
  selectedGroupName,
  showGroupDropdown,
  setShowGroupDropdown,
  groupSearch,
  setGroupSearch,
  groupDropdownRef,
  groupDropUp,
  groupMenuMaxH,
  openGroupDropdown,
  filteredGroupedByExchange,
  handleSelectGroup,
  groupStocksLoading,
  groupStocks,
  groupStockSearch,
  setGroupStockSearch,
  filteredGroupStocks,
}) => {
  // Group symbols by source for categorized display.
  const symbolsByCategory = useMemo(() => {
    const groups = new Map(); // key = "source|sourceLabel"
    for (const sym of formData.symbols) {
      const meta = symbolSources.get(sym) || { source: 'search', sourceLabel: null };
      const key = meta.source === 'search'
        ? 'search|'
        : `${meta.source}|${meta.sourceLabel || ''}`;
      if (!groups.has(key)) {
        groups.set(key, { source: meta.source, sourceLabel: meta.sourceLabel, symbols: [] });
      }
      groups.get(key).symbols.push(sym);
    }
    // Order: index first, watchlist second, search last
    const order = { index: 0, watchlist: 1, search: 2 };
    return [...groups.values()].sort(
      (a, b) => (order[a.source] ?? 9) - (order[b.source] ?? 9)
    );
  }, [formData.symbols, symbolSources]);

  // Source display config
  const sourceConfig = {
    index:     { icon: <BarChartIcon width={14} height={14} />, prefix: 'Index' },
    watchlist: { icon: <BookmarkIcon width={14} height={14} />, prefix: 'Watchlist' },
    search:    { icon: <MagnifyingGlassIcon width={14} height={14} />, prefix: 'Search' },
  };

  return (
    <div className={styles.stepContent}>
      <h2 className={styles.stepTitle}>Basic Information</h2>
      <p className={styles.stepDescription}>Set up the strategy name and symbol.</p>

      <div className={styles.fieldRow} style={{ alignItems: 'flex-start' }}>
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Strategy Name *</label>
          <input
            type="text"
            className={styles.input}
            placeholder="e.g., RSI Overbought Alert"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
          />
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label}>Timeframe *</label>
          <select
            className={styles.select}
            value={formData.timeframe || '1m'}
            onChange={(e) => updateField('timeframe', e.target.value)}
          >
            <option value="1m">1 Minute</option>
            <option value="5m">5 Minutes</option>
            <option value="15m">15 Minutes</option>
            <option value="1h">1 Hour</option>
            <option value="1d">1 Day</option>
          </select>
        </div>
      </div>

      <div className={styles.fieldGroup} ref={symbolDropdownRef}>
        <label className={styles.label}>
          Symbols *
          <span className={styles.symbolCounter}>
            {formData.symbols.length} selected
          </span>
        </label>

        {/* Search input + suggestion dropdown */}
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            className={styles.input}
            placeholder={
              formData.symbols.length === 0
                ? 'Search symbol... (e.g., RELIANCE)'
                : 'Add another symbol...'
            }
            value={symbolSearch}
            onFocus={() => setShowSymbolDropdown(true)}
            onKeyDown={(e) => { if (e.key === 'Escape') setShowSymbolDropdown(false); }}
            onChange={(e) => {
              setSymbolSearch(e.target.value);
              setShowSymbolDropdown(true);
            }}
          />
          {showSymbolDropdown && filteredSymbols.length > 0 && (
            <div style={{
              position: 'absolute', width: '100%', top: '100%', left: 0,
              background: 'white', border: '1px solid #ddd', borderRadius: 8,
              maxHeight: 200, overflowY: 'auto', marginTop: 4, zIndex: 100,
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            }}>
              {filteredSymbols.slice(0, 50).map((item) => (
                <div
                  key={item.value}
                  style={{ padding: '10px 12px', cursor: 'pointer', fontSize: 14, transition: '0.15s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f2f6ff'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  onClick={() => {
                    addSymbol(item.value, 'search', null);
                    setSymbolSearch('');
                  }}
                >
                  {item.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Watchlist + Index/Group pickers share one horizontal row */}
      <div className={styles.fieldRow} style={{ alignItems: 'flex-start' }}>

        {/* Add from Watchlist */}
        <div className={styles.fieldGroup}>
          <label className={styles.label}>Add from Watchlist <span className={styles.optionalTag}>(Optional)</span></label>
          {watchlistsLoading ? (
            <select className={styles.select} disabled>
              <option>Loading watchlists…</option>
            </select>
          ) : watchlistsError ? (
            <select className={styles.select} disabled>
              <option>{watchlistsError}</option>
            </select>
          ) : (
            <select
              className={styles.select}
              value={selectedWatchlistId}
              onChange={(e) => handleSelectWatchlist(e.target.value)}
            >
              <option value="">{watchlists.length === 0 ? 'No watchlists found' : 'Select a watchlist…'}</option>
              {watchlists.map((wl) => (
                <option key={wl.wishlist_id} value={wl.wishlist_id}>
                  {wl.wishlist_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Add by Index / Group */}
        <div className={styles.fieldGroup} style={{ order: -1 }}>
          <label className={styles.label}>Add by Index / Group <span className={styles.optionalTag}>(Optional)</span></label>
          {groupsLoading ? (
            <input type="text" className={styles.input} placeholder="Loading groups…" disabled />
          ) : groupsError ? (
            <input type="text" className={styles.input} placeholder={groupsError} disabled style={{ color: '#ef4444' }} />
          ) : groups.length === 0 ? (
            <input type="text" className={styles.input} placeholder="No groups available" disabled />
          ) : (
          <div style={{ position: 'relative' }} ref={groupDropdownRef}>
            <input
              type="text"
              className={styles.input}
              placeholder="Select a group…"
              value={showGroupDropdown ? groupSearch : selectedGroupName}
              onFocus={openGroupDropdown}
              onChange={(e) => { setGroupSearch(e.target.value); setShowGroupDropdown(true); }}
              onKeyDown={(e) => { if (e.key === 'Escape') setShowGroupDropdown(false); }}
            />
            {showGroupDropdown && (
              <div style={{
                position: 'absolute', width: '100%', left: 0,
                ...(groupDropUp
                  ? { bottom: '100%', marginBottom: 4 }
                  : { top: '100%', marginTop: 4 }),
                background: 'white', border: '1px solid #ddd', borderRadius: 8,
                maxHeight: groupMenuMaxH, overflowY: 'auto', zIndex: 100,
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              }}>
                {filteredGroupedByExchange.length === 0 ? (
                  <div style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>No groups found</div>
                ) : filteredGroupedByExchange.map(({ exchange, items }) => (
                  <div key={exchange}>
                    <div style={{
                      padding: '6px 12px', fontSize: 11, fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: 0.4,
                      color: '#6b7280', background: '#f9fafb', fontStyle: 'normal',
                      position: 'sticky', top: 0,
                    }}>
                      {exchange}
                    </div>
                    {items.map(({ idx, name }) => (
                      <div
                        key={idx}
                        onClick={() => { handleSelectGroup(String(idx)); setShowGroupDropdown(false); setGroupSearch(''); }}
                        style={{ padding: '8px 12px 8px 20px', cursor: 'pointer', fontSize: 14, transition: '0.15s' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f2f6ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                      >
                        {name}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      </div>


      {/* StockPicker blocks removed — symbols from index/group and
          watchlist are shown in the categorized "Selected Symbols"
          section below, so the inline picker grid was redundant. */}

      {/* ── Categorized symbols display ────────────────────── */}
      {symbolsByCategory.length > 0 && (
        <div className={styles.fieldGroup} style={{ marginTop: '64px' }}>
          <label className={styles.label}>Selected Symbols</label>
          <div className={styles.symbolCategories}>
            {symbolsByCategory.map((cat) => {
              const cfg = sourceConfig[cat.source] || sourceConfig.search;
              const label = cat.sourceLabel
                ? `${cfg.prefix} · ${cat.sourceLabel}`
                : cfg.prefix;
              return (
                <div
                  key={`${cat.source}|${cat.sourceLabel || ''}`}
                  className={`${styles.symbolCategory} ${styles[`symbolCategory_${cat.source}`] || ''}`}
                >
                  <div className={styles.symbolCategoryHeader}>
                    <span className={styles.symbolCategoryIcon}>{cfg.icon}</span>
                    <span className={styles.symbolCategoryLabel}>{label}</span>
                    <span className={styles.symbolCategoryCount}>
                      {cat.symbols.length} {cat.symbols.length === 1 ? 'stock' : 'stocks'}
                    </span>
                    <button
                      type="button"
                      className={styles.symbolCategoryRemove}
                      onClick={() => removeCategory(cat.source, cat.sourceLabel)}
                      title={`Remove all ${label} symbols`}
                    >
                      <Cross2Icon width={14} height={14} />
                    </button>
                  </div>
                  <div className={styles.symbolCategoryChips}>
                    {cat.symbols.map((sym) => (
                      <label key={sym} className={styles.symbolCheckItem}>
                        <Checkbox.Root
                          className={styles.checkboxRoot}
                          checked={true}
                          onCheckedChange={(checked) => {
                            if (!checked) removeSymbol(sym);
                          }}
                        >
                          <Checkbox.Indicator className={styles.checkboxIndicator}>
                            <CheckIcon width={12} height={12} />
                          </Checkbox.Indicator>
                        </Checkbox.Root>
                        <span className={styles.symbolCheckLabel}>{sym}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default StepBasicInfo;
